import React from "react";
import VariantPanel, { type VariantFact } from "@/components/common/VariantPanel";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface SieveBendCopy {
  eyebrow: string;
  title: string;
  lead: string;
  points: string[];
  facts: VariantFact[];
  imageAlt: string;
}
const copyData: Record<Lang, SieveBendCopy> = {
  en: {
    eyebrow: "Static Gravity Separation • DSM Sieve Bends",
    title: "Static Sieve Bend Screens",
    lead: "MutualFilter static sieve bends utilize curved cross-flow wedge wire profiles to maximize hydro-extraction and particle retention without mechanical drives or electric power. Manufactured with precise aperture tolerances for reliable dewatering and classification.",
    points: ["Operates under static gravity flow with zero motor drives, minimizing power consumption and maintenance downtime.", "Continuous non-clogging open area across curved profile strips liquid through slots while solids slide down.", "Engineered replacement panels and complete housings with feed box, weir distributor, and splash covers available."],
    facts: [{
      label: "Slot Width",
      value: "0.25 – 3.0 mm"
    }, {
      label: "Standard Arc Angles",
      value: "45° / 60° / 120°"
    }, {
      label: "Feed Mechanism",
      value: "Static Gravity Flow"
    }, {
      label: "Alloy Grades",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Industrial stainless steel curved sieve bend screen panel for solid liquid dewatering"
  },
  zh: {
    eyebrow: "静态重力自流分离 • DSM 弧形筛弯",
    title: "静态弧形筛板系统 (Sieve Bend)",
    lead: "MutualFilter 静态弧形筛（DSM 筛）利用精密弯曲横流楔形丝型材，无需机械驱动或电力消耗即可实现高效水力脱水与固体物料截留。结构坚固、流道畅通，广泛应用于选矿、淀粉、造纸及水处理脱水。",
    points: ["全重力自流静态运行，无运动驱动机构，彻底降低电力消耗与运行维护停机频次。", "弧形截面连续自清洁开孔结构，切刃效应剥离液膜顺畅穿过筛缝，固体颗粒沿筛面加速滑落。", "支持定制替换筛板及成套筛箱壳体总成，配套溢流堰布料箱、防溅盖板与标准化排料口。"],
    facts: [{
      label: "筛缝宽度",
      value: "0.25 – 3.0 mm"
    }, {
      label: "标准弧度规格",
      value: "45° / 60° / 120°"
    }, {
      label: "进料方式",
      value: "静态重力自流"
    }, {
      label: "标准材质",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "工业不锈钢重力脱水弧形筛板（DSM筛）"
  },
  es: {
    eyebrow: "Separación estática por gravedad • Cribas DSM",
    title: "Cribas curvas estáticas sieve bend",
    lead: "Las cribas curvas estáticas MutualFilter utilizan perfiles de alambre en cuña de flujo cruzado para maximizar el drenaje y la retención de sólidos sin motores ni consumo eléctrico, proporcionando deshidratación y clasificación precisa.",
    points: ["Operación por gravedad estática sin accionamientos mecánicos, eliminando consumo eléctrico y paradas operativas.", "Área abierta continua y autolimpiante que drena el líquido por las ranuras mientras los sólidos se deslizan por la criba.", "Paneles de sustitución y conjuntos completos con caja de alimentación, vertedero distribuidor y tapas antisalpicaduras."],
    facts: [{
      label: "Ancho de ranura",
      value: "0,25 – 3,0 mm"
    }, {
      label: "Ángulos de arco",
      value: "45° / 60° / 120°"
    }, {
      label: "Alimentación",
      value: "Gravedad estática"
    }, {
      label: "Aleaciones",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Panel de criba curva de acero inoxidable para deshidratación sólido-líquido"
  },
  de: {
    eyebrow: "Statische Schwerkrafttrennung • DSM-Bogensebe",
    title: "Statische Bogensiebe (Sieve Bend)",
    lead: "Statische Bogensiebe von MutualFilter nutzen gekrümmte Querstrom-Spaltsiepprofile für maximale Entwässerung und Feststoffrückhaltung ohne Antriebe oder Stromverbrauch. Zuverlässige Fest-Flüssig-Trennung für anspruchsvolle Prozesslinien.",
    points: ["Rein schwerkraftbetriebene statische Trennung ohne Motoren oder Getriebe für minimalen Energie- und Wartungsaufwand.", "Kontinuierlich verstopfungsfreie offene Fläche trennt Flüssigkeitsfilme ab, während Feststoffe über die Krümmung abgleiten.", "Austauschsiebbeläge sowie komplette Baugruppen mit Einlaufkasten, Überlaufwehr und Spritzschutzabdeckungen lieferbar."],
    facts: [{
      label: "Spaltweite",
      value: "0,25 – 3,0 mm"
    }, {
      label: "Bogenwinkel",
      value: "45° / 60° / 120°"
    }, {
      label: "Zulaufart",
      value: "Statischer Schwerkraftfluss"
    }, {
      label: "Werkstoffe",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Industrielle Edelstahl-Bogensiebplatte zur Schwerkraftentwässerung"
  },
  fr: {
    eyebrow: "Séparation gravitaire statique • Tamis courbes DSM",
    title: "Grilles courbes statiques sieve bend",
    lead: "Les grilles courbes statiques MutualFilter utilisent des profilés en V à écoulement transversal pour maximiser le drainage et la rétention de particules sans moteur ni électricité. Conçues pour un égouttage continu et une classification fiable.",
    points: ["Fonctionnement statique gravitaire sans motorisation mécanique, réduisant la consommation électrique et les coûts de maintenance.", "Surface ouverte continue anti-colmatage drainant les phases liquides à travers les fentes tandis que les solides glissent.", "Panneaux de remplacement et caissons complets avec boîte d'alimentation, déversoir régulateur et capots anti-éclaboussures."],
    facts: [{
      label: "Largeur de fente",
      value: "0,25 – 3,0 mm"
    }, {
      label: "Angles d'arc",
      value: "45° / 60° / 120°"
    }, {
      label: "Alimentation",
      value: "Écoulement gravitaire statique"
    }, {
      label: "Nuances d'alliage",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Panneau de grille courbe en acier inoxydable pour séparation gravitaire"
  },
  pt: {
    eyebrow: "Separação por gravidade estática • Peneiras DSM",
    title: "Peneiras curvas estáticas sieve bend",
    lead: "As peneiras curvas estáticas da MutualFilter utilizam perfis em cunha de fluxo cruzado para maximizar a drenagem e retenção de sólidos sem acionamentos mecânicos ou consumo elétrico, garantindo desaguamento e classificação contínuos.",
    points: ["Operação por gravidade estática sem acionamentos mecânicos, minimizando o consumo energético e as paradas operacionais.", "Área aberta contínua e autolimpante que extrai o fluido através das fendas enquanto os sólidos deslizam pela tela.", "Painéis sobressalentes e carcaças completas com caixa de alimentação, vertedouro distribuidor e coberturas antirrespingo."],
    facts: [{
      label: "Largura de fenda",
      value: "0,25 – 3,0 mm"
    }, {
      label: "Ângulos de arco",
      value: "45° / 60° / 120°"
    }, {
      label: "Mecanismo",
      value: "Fluxo por gravidade estático"
    }, {
      label: "Ligas de aço",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Painel de peneira curva em aço inoxidável para desaguamento sólido-líquido"
  }
};
export default function SieveBendGroup() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <VariantPanel id="sieve-bend" href="/SieveBendScreensPage" eyebrow={copy.eyebrow} title={copy.title} lead={copy.lead} points={copy.points} facts={copy.facts} image={{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
    alt: copy.imageAlt
  }} tone="muted" />;
}